import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import LandingPageWrapper from "../../../pages/AppPages/Pages/components/LandingPageWrapper";
import { FastCheckoutMachineProvider } from "../../CheckoutMachine";
import { CheckoutDetail } from "./CheckoutDetail";
import { getUserSignedInState } from "@/Auth";
import Loading from "../../../Loading";
import { useTrackActions } from "@/utils/hooks/useTracking";
import { AnalyticsTrackingEvent } from "@brandclub/common-ui";

type UserLoginStatus = "notChecked" | "loggedIn" | "notLoggedIn";

const CheckoutSummary = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");
  const [userLoginStatus, setUserLoginStatus] =
    useState<UserLoginStatus>("notChecked");
  const navigate = useNavigate();

  const [trackAction] = useTrackActions();

  useEffect(() => {
    if (orderId) {
      trackAction?.(
        AnalyticsTrackingEvent.CHECKOUT_CONNECT_SUMMARY_SCREEN_VISIT,
        {
          orderId,
        }
      );
    }
  }, [orderId, trackAction]);

  useEffect(() => {
    const checkUserInfo = async () => {
      try {
        const { userInfo, signedIn } = await getUserSignedInState();
        if (userInfo && signedIn) {
          setUserLoginStatus("loggedIn");
          return;
        }
      } catch (error) {
        console.error(error);
      }
      // this route requires login, so redirect to login page
      setUserLoginStatus("notLoggedIn");
      // Use replace: true to prevent back navigation to this page
      navigate("/signin", {
        replace: true,
        state: {
          redirectPath: {
            pathname: "/checkoutSummary",
            search: searchParams.toString(),
          },
        },
      });
    };

    checkUserInfo();
  }, [navigate, searchParams]);

  useEffect(() => {
    if (orderId === null && userLoginStatus === "loggedIn") {
      console.warn("no order id found in the URL; navigating to purchase");
      navigate("/dashboard/purchase");
    }
  }, [orderId, navigate, userLoginStatus]);

  if (userLoginStatus === "notChecked") {
    return <Loading />;
  }

  if (userLoginStatus === "notLoggedIn") {
    return null;
  }

  if (!orderId) {
    return null;
  }

  return (
    <FastCheckoutMachineProvider orderId={orderId}>
      <LandingPageWrapper includeFooter={false}>
        <CheckoutDetail />
      </LandingPageWrapper>
    </FastCheckoutMachineProvider>
  );
};

export default CheckoutSummary;
